import * as React from 'react';

import * as ramda from 'ramda';

import type {
    ReportMap,
    ChartBucketReportData,
    ChartBucketData,
    ComparisonRangeType,
} from 'nutshell-core/types';

// @ts-expect-error
import {getInstanceCurrencyPrefix} from 'shells/utils/currency';

import {getTooltipUnit, isMarketingReportKey, QUOTES, INVOICES} from '../../report-constants';
import type {ReportGap, ReportInfo, DateRangeType, ReportKey} from '../../report-types';

import {TooltipDeltas} from './tooltip-deltas';
import {TooltipLabels} from './tooltip-labels';
import {TooltipTable} from './tooltip-table';
import type {TooltipItem, TooltipRowData} from './tooltip-types';
import {isItemQuota, isItemReference, prettifyDateFilters} from './tooltip-utils';

import styles from './tooltips.module.css';

export function renderTooltip(
    dateRangeType: DateRangeType,
    reportMaps: ReportInfo,
    reportGap: ReportGap,
    isSegmented: boolean,
    useConfidence: boolean | null | undefined,
    compareTo: ComparisonRangeType | null | undefined,
    reportKey: ReportKey
) {
    type TooltipProps = {
        active: boolean;
        payload: Array<any>;
        label: string;
    };

    return function Tooltip(props: TooltipProps) {
        if (!props.active || !props.payload) return null;

        // Reverse the order so that they render in the same order as the chart
        const items: Array<TooltipItem> = isMarketingReportKey(reportKey)
            ? props.payload
            : ramda.reverse(props.payload);
        if (!items.length) return null;

        const isComparing = Boolean(compareTo);
        // @ts-expect-error
        const bucketData: ChartBucketData = items[0].payload.bucketData;
        // @ts-expect-error
        const referenceBucketData = items[0].payload.referenceBucketData;
        const reportData: ChartBucketReportData = bucketData && bucketData.reportData;
        const referenceReportData: ChartBucketReportData =
            referenceBucketData && referenceBucketData.reportData;
        const currencyPrefix = getInstanceCurrencyPrefix();

        // Detail rows
        const itemsWithoutQuota = items.filter(
            (item) => !isItemQuota(item) && !isItemReference(item)
        );
        let currentDetailRows: Array<TooltipRowData> | null | undefined;
        if (bucketData) {
            currentDetailRows = itemsWithoutQuota.map((item) => {
                let rowData;
                if (isSegmented) {
                    const summary = useConfidence ? bucketData.summaryWeighted : bucketData.summary;
                    rowData = {
                        count:
                            summary && summary.segments[item.name]
                                ? // @ts-expect-error
                                  summary.segments[item.name].count
                                : null,
                        total:
                            summary && summary.segments[item.name]
                                ? // @ts-expect-error
                                  summary.segments[item.name].total
                                : null,
                    };
                } else {
                    const report = findReportByName(reportMaps.reports, item.name);
                    rowData = {
                        // @ts-expect-error
                        count: report && reportData[report.id] ? reportData[report.id].count : null,
                        // @ts-expect-error
                        total: report && reportData[report.id] ? reportData[report.id].total : null,
                    };
                }

                return {
                    name: item.name,
                    color: item.color,
                    iconStyle: 'square',
                    unit: getTooltipUnit(reportKey),
                    rowData,
                };
            });
        }

        // Reference detail rows
        let referenceDetailRows: Array<TooltipRowData> | null | undefined;
        if (isComparing && reportMaps.referenceReports && referenceReportData) {
            const refReportInfo = reportMaps.referenceReports;
            referenceDetailRows = itemsWithoutQuota.map((item) => {
                const report = findReportByName(refReportInfo, item.name);
                const isActivityReport = report && report.key === 'activity';
                let referenceData;
                if (isSegmented) {
                    const summary = useConfidence
                        ? referenceBucketData.summaryWeighted
                        : referenceBucketData.summary;
                    if (!summary) {
                        referenceData = {
                            count: null,
                            total: null,
                        };
                    } else if (!summary.segments[item.name]) {
                        // This means that the segment was not found in reference, so it must have been zero!
                        referenceData = {
                            count: 0,
                            total: !isActivityReport ? 0 : null,
                        };
                    } else {
                        referenceData = {
                            count: summary.segments[item.name].count,
                            total: summary.segments[item.name].total,
                        };
                    }
                } else {
                    referenceData = {
                        // @ts-expect-error
                        count: report ? referenceReportData[report.id].count : null,
                        // @ts-expect-error
                        total: report ? referenceReportData[report.id].total : null,
                    };
                }

                return {
                    name: item.name,
                    color: item.color,
                    iconStyle: 'square',
                    unit: getTooltipUnit(reportKey),
                    rowData: referenceData,
                };
            });
        }

        // Quota row
        let quotaRow: TooltipRowData | null | undefined;
        const quotaItem = items.find((item) => isItemQuota(item));
        if (quotaItem && bucketData) {
            const quotaReport = findReportByName(reportMaps.reports, quotaItem.name);
            // Activity quotas are by `count`, and leads reports are by `total` facet
            // Check whether there's also an Activities report, so we know what kind
            // of quota report we're dealing with
            const isActivityReport = Boolean(findReportByName(reportMaps.reports, 'Activities'));
            quotaRow = {
                name: quotaItem.name,
                color: quotaItem.color,
                unit: isActivityReport ? null : 'leads',
                iconStyle: 'dot',
                rowData: {
                    count:
                        // @ts-expect-error
                        isActivityReport && quotaReport ? reportData[quotaReport.id].count : null,
                    total:
                        // @ts-expect-error
                        !isActivityReport && quotaReport ? reportData[quotaReport.id].total : null,
                },
            };
        }

        // Totals rows
        let currentTotalRow;
        if (bucketData) {
            const summary = useConfidence ? bucketData.summaryWeighted : bucketData.summary;
            currentTotalRow = {
                count: summary && summary.count,
                total: summary && summary.total,
            };
        }

        let referenceTotalRow;
        if (referenceDetailRows) {
            const referenceSummary = useConfidence
                ? referenceBucketData.summaryWeighted
                : referenceBucketData.summary;
            referenceTotalRow = {
                count: referenceSummary && referenceSummary.count,
                total: referenceSummary && referenceSummary.total,
            };
        }

        // Delta rows
        let deltaRows;
        if (isComparing && currentDetailRows && currentTotalRow) {
            const facet = currentDetailRows.every((row) => row.rowData.total !== null)
                ? 'total'
                : 'count';
            deltaRows = currentDetailRows.map((row, rowIndex) => {
                return {
                    name: row.name,
                    current: row.rowData[facet] || 0,
                    reference: referenceDetailRows
                        ? // @ts-expect-error
                          referenceDetailRows[rowIndex].rowData[facet] || 0
                        : 0,
                    isTotal: false,
                };
            });
            if (currentDetailRows.length > 1) {
                deltaRows.push({
                    name: 'total',
                    current: currentTotalRow[facet] || 0,
                    reference: (referenceTotalRow && referenceTotalRow[facet]) || 0,
                    isTotal: true,
                });
            }
        }

        if (isComparing && referenceDetailRows) {
            const referenceHeader =
                prettifyDateFilters(referenceBucketData.filters, dateRangeType) ||
                // @ts-expect-error
                items[0].payload.referenceName;
            const currentHeader = bucketData
                ? // @ts-expect-error
                  prettifyDateFilters(bucketData.filters, dateRangeType) || items[0].payload.name
                : undefined;
            const detailRowCount =
                (currentDetailRows && currentDetailRows.length) || referenceDetailRows.length;

            const includeTotal =
                detailRowCount > 1 && reportKey !== QUOTES && reportKey !== INVOICES;

            return (
                <div className={styles['tooltip-container--compare-mode']}>
                    <div className={styles['tooltip-table-comparison-container--reference']}>
                        <TooltipLabels
                            detailRows={currentDetailRows || referenceDetailRows}
                            includeTotal={includeTotal}
                            quotaRow={quotaRow}
                            headingText='Comparing…'
                        />
                        <TooltipTable
                            detailRows={referenceDetailRows}
                            totalRow={includeTotal ? referenceTotalRow : null}
                            currencyPrefix={currencyPrefix}
                            headingText={referenceHeader}
                        />
                    </div>
                    {currentDetailRows && deltaRows ? (
                        <div className={styles['tooltip-table-comparison-container--current']}>
                            <TooltipTable
                                detailRows={currentDetailRows}
                                totalRow={detailRowCount > 1 ? currentTotalRow : null}
                                quotaRow={quotaRow}
                                currencyPrefix={currencyPrefix}
                                headingText={currentHeader}
                            />
                            <TooltipDeltas deltaRows={deltaRows} />
                        </div>
                    ) : (
                        <div
                            className={styles['tooltip-table-comparison-container--current-empty']}
                        />
                    )}
                </div>
            );
        }

        // Non-compare mode
        if (currentDetailRows) {
            const mainHeading =
                prettifyDateFilters(bucketData.filters, dateRangeType) || props.label;

            const editionReport = findReportByName(reportMaps.reports, 'Edition');
            const visitsReport = findReportByName(reportMaps.reports, 'McfxVisit');

            const hideTotals =
                (editionReport && editionReport.key === 'edition') ||
                (visitsReport && visitsReport.key === 'siteAnalytics') ||
                reportKey === QUOTES ||
                reportKey === INVOICES;

            return (
                <div className={styles['tooltip-container']}>
                    <TooltipLabels
                        detailRows={currentDetailRows}
                        includeTotal={currentDetailRows.length > 1 && !hideTotals}
                        quotaRow={quotaRow}
                        headingText={mainHeading}
                    />
                    <TooltipTable
                        detailRows={currentDetailRows}
                        totalRow={
                            currentDetailRows.length > 1 && !hideTotals ? currentTotalRow : null
                        }
                        quotaRow={quotaRow}
                        currencyPrefix={currencyPrefix}
                    />
                </div>
            );
        }

        return null;
    };
}

function findReportByName(reportMaps: Array<ReportMap>, name: string) {
    return reportMaps.find((rep) => rep.name === name);
}
